import React, { ReactNode, useEffect } from "react";
import { Button, Dialog, DialogContent, IconButton, useMediaQuery } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DangerAlertIcon from "../../images/dangeralert.png";

interface ActionDialogProps {
  open: boolean;
  onClose: () => void;
  /** Icon element to display at the top (e.g. <MailOutlineIcon />) */
  icon?: ReactNode;
  /** Background color for the circular icon container */
  iconBgColor?: string;
  /** Size of the icon circle in px */
  iconSize?: number;
  /** Use a preset icon variant instead of custom icon */
  iconVariant?: "success" | "error";
  /** Dialog title */
  title?: string;
  /** Dialog description text */
  description?: string | ReactNode;
  /** Primary action button label */
  buttonText?: string;
  /** Primary action button click handler */
  onButtonClick?: () => void;
  /** Primary button background color (default: #E1A731) */
  buttonColor?: string;
  /** Secondary action button label */
  secondaryButtonText?: string;
  /** Secondary action button click handler */
  onSecondaryButtonClick?: () => void;
  /** Secondary button background color (default: #000000) */
  secondaryButtonColor?: string;
  /** Auto-dismiss after ms (0 = no auto-dismiss) */
  autoDismissMs?: number;
  /** Max width of dialog */
  maxWidth?: string;
}

const ActionDialog: React.FC<ActionDialogProps> = ({
  open,
  onClose,
  icon,
  iconBgColor = "#4CAF50",
  iconSize = 80,
  iconVariant,
  title,
  description,
  buttonText,
  onButtonClick,
  buttonColor = "#E1A731",
  secondaryButtonText,
  onSecondaryButtonClick,
  secondaryButtonColor = "#000000",
  autoDismissMs = 0,
  maxWidth = "480px",
}) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  useEffect(() => {
    if (open && autoDismissMs > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoDismissMs);
      return () => clearTimeout(timer);
    }
  }, [open, autoDismissMs, onClose]);

  const renderIcon = () => {
    if (iconVariant === "error") {
      return (
        <img
          src={DangerAlertIcon}
          alt="Error"
          style={{
            width: "105px",
            height: "105px",
            marginBottom: "16px",
          }}
        />
      );
    }

    if (iconVariant === "success") {
      return (
        <div
          style={{
            width: "105px",
            height: "105px",
            borderRadius: "50%",
            backgroundColor: "#00B621",
            border: "1px solid #FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <svg
            width="60"
            height="60"
            viewBox="0 0 60 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="30"
              cy="30"
              r="25"
              stroke="#FFFFFF"
              strokeWidth="4"
              fill="none"
            />
            <path
              d="M18 30L26 38L42 22"
              stroke="#FFFFFF"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </div>
      );
    }

    if (icon) {
      return (
        <div
          style={{
            width: `${iconSize}px`,
            height: `${iconSize}px`,
            borderRadius: "50%",
            backgroundColor: iconBgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          {icon}
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        style: {
          borderRadius: "16px",
          padding: isMobile ? "24px 18px" : "32px",
          maxWidth,
          width: "100%",
          margin: isMobile ? "16px" : undefined,
          textAlign: "center",
          position: "relative",
          overflow: "visible",
        },
      }}
    >
      <IconButton
        onClick={onClose}
        style={{
          position: "absolute",
          top: "8px",
          right: "8px",
          width: "30px",
          height: "30px",
          backgroundColor: "#ED1C24",
          borderRadius: "100px",
          color: "#FFFFFF",
          padding: 0,
          zIndex: 1,
          boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
        }}
      >
        <CloseIcon style={{ fontSize: "18px" }} />
      </IconButton>

      <DialogContent style={{ padding: 0, textAlign: "center" }}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          {renderIcon()}
        </div>

        {title && (
          <p
            style={{
              fontFamily: "DM Sans, sans-serif",
              fontSize: "24px",
              fontWeight: 700,
              color: "#24272C",
              marginBottom: "12px",
              marginTop: 0,
            }}
          >
            {title}
          </p>
        )}

        {description && (
          typeof description === "string" ? (
            <p
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "16px",
                lineHeight: "24px",
                color: "#666666",
                marginBottom: buttonText ? "24px" : "0",
                marginTop: 0,
              }}
            >
              {description}
            </p>
          ) : (
            <div style={{ marginBottom: buttonText ? "24px" : "0" }}>
              {description}
            </div>
          )
        )}

        {(buttonText || secondaryButtonText) && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "12px",
              flexWrap: "nowrap",
            }}
          >
            {buttonText && (
              <Button
                onClick={onButtonClick}
                style={{
                  flex: 1,
                  backgroundColor: buttonColor,
                  color: "#FFFFFF",
                  borderRadius: "8px",
                  padding: isMobile ? "10px 8px" : "12px 16px",
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: 700,
                  fontFamily: "DM Sans, sans-serif",
                  textTransform: "none",
                  minWidth: 0,
                  lineHeight: 1.2,
                  whiteSpace: isMobile ? "normal" : "nowrap",
                }}
              >
                {buttonText}
              </Button>
            )}
            {secondaryButtonText && (
              <Button
                onClick={onSecondaryButtonClick}
                style={{
                  flex: 1,
                  backgroundColor: secondaryButtonColor,
                  color: "#FFFFFF",
                  borderRadius: "8px",
                  padding: isMobile ? "10px 8px" : "12px 16px",
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: 700,
                  fontFamily: "DM Sans, sans-serif",
                  textTransform: "none",
                  minWidth: 0,
                  lineHeight: 1.2,
                  whiteSpace: isMobile ? "normal" : "nowrap",
                }}
              >
                {secondaryButtonText}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ActionDialog;
